import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, oidcAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';

import {
  AlertDisplay,
  AutoLogout,
  OAuthRequestDialog,
  SignInPage,
  SignInProviderConfig,
} from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { tietoTheme } from './theme/tietoTheme';
import { UnifiedThemeProvider } from '@backstage/theme';
import LightIcon from '@material-ui/icons/WbSunny';
import { CustomTechDocsHome } from './components/techdocs/CustomTechDocsHome';
import { CustomTecDocsReaderPage } from './components/techdocs/CustomTecDocsReaderPage';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';

const prodAuthProvider: SignInProviderConfig = {
  id: 'oidc',
  title: 'Lifecare Open Platform',
  message: 'Please wait while we redirect you to our authentication portal. ',
  apiRef: oidcAuthApiRef,
};

// const devAuthProviders: IdentityProviders = [
//   'guest',
//   {
//     id: 'microsoft',
//     title: 'Microsoft Authentication',
//     message: 'Sign in using Microsoft authentication',
//     apiRef: microsoftAuthApiRef,
//   },
// ];

// const prodAuthProvider: SignInProviderConfig = {
//   id: 'microsoft',
//   title: 'Microsoft Authentication',
//   message: 'Sign in using Microsoft authentication',
//   apiRef: microsoftAuthApiRef,
// };

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage auto 
        align="center"
        title="Lifecare Open Platform"
        {...props}
        provider={prodAuthProvider}
        // providers={['guest']}
      />
    ),
  },
  themes: [
    {
      id: 'tieto-Theme',
      title: 'My Custom Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={tietoTheme} children={children} />
      ),
    },
  ],
});

const ConfiguredAutoLogout = () => {
  const config = useApi(configApiRef);
  const idleTimeoutMinutes = config.getOptionalNumber('app.autoLogout.idleTimeoutMinutes') ?? 90;
  const promptBeforeIdleSeconds = config.getOptionalNumber('app.autoLogout.promptBeforeIdleSeconds') ?? 30;
  return (
    <AutoLogout
      useWorkerTimers={false}
      idleTimeoutMinutes={idleTimeoutMinutes}
      promptBeforeIdleSeconds={promptBeforeIdleSeconds}
    />
  );
};

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="docs" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>

    <Route path="/docs" element={<TechDocsIndexPage />}>
      <CustomTechDocsHome />
      <TechDocsAddons>
        <Mermaid
          config={{ theme: 'forest', themeVariables: { lineColor: '#000000' } }}
        />
      </TechDocsAddons>
    </Route>

    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <CustomTecDocsReaderPage />
      <TechDocsAddons>
        <Mermaid
          config={{ theme: 'forest', themeVariables: { lineColor: '#000000' } }}
        />
      </TechDocsAddons>
    </Route>

    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <ConfiguredAutoLogout />
    <AlertDisplay />
    <SignalsDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      {/* <Root>{routes}</Root> */}
      {routes}
    </AppRouter>
  </>,
);
